import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import correctSound from "../../Assets/final new sound/correct-2-46134.mp3";
import wrongSound from "../../Assets/final new sound/invalid-selection-39351.mp3";
import AuthUser from "../../Hooks/authUser";
import moment from "moment";
import { server_url } from "../../Config/API";
import UpdateHooks from "../../Hooks/UpdateHooks";
import PostHooks from "../../Hooks/PostHooks";
import GameStatsIndicator from "./GameStatsIndicator";

const CircleTypingGame = () => {
  const [targetLetters, setTargetLetters] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300);
  const [score, setScore] = useState(0);
  const [rockChartData, setRockChartData] = useState([]);

  const correctAudioRef = useRef(null);
  const wrongAudioRef = useRef(null);
  if (typeof window !== "undefined") {
    if (!correctAudioRef.current) correctAudioRef.current = new Audio(correctSound);
    if (!wrongAudioRef.current) wrongAudioRef.current = new Audio(wrongSound);
  }

  const { userInfo } = AuthUser();
  const date = useRef(moment().format("YYYY-MM-D")).current;
  const time = useRef(moment().format("h:mm a")).current;

  const previousStateRef = useRef(null);
  const scoreRef = useRef(score);
  const timeRef = useRef(timeLeft);
  const currentIndexRef = useRef(currentIndex);
  const targetLettersRef = useRef(targetLetters);

  scoreRef.current = score;
  timeRef.current = timeLeft;
  currentIndexRef.current = currentIndex;
  targetLettersRef.current = targetLetters;

  const fetchFunction = async () => {
    try {
      const response = await fetch(
        `${server_url}/rockChart/specific?fieldName=userId&fieldValue=${
          userInfo?._id
        }&fieldName2=date&fieldValue2=${date}&fieldName3=gameName&fieldValue3=Rock+Chart+Monocular`
      );
      const data = await response.json();
      setRockChartData(data?.data || []);
      if (data?.data && data?.data.length) {
        setScore(data?.data[0]?.score);
        previousStateRef.current = data?.data;
      }
    } catch (error) {
      console.error("Error fetching rock chart data:", error);
    }
  };

  useEffect(() => {
    if (rockChartData?.length) {
      setTimeLeft(rockChartData[0]?.remainingTime);
      setScore(rockChartData[0]?.score);
    }
  }, [rockChartData]);

  useEffect(() => {
    fetchFunction();
  }, []);

  const insertFunction = useCallback(() => {
    if (previousStateRef?.current?.length) {
      UpdateHooks(
        `${server_url}/rockChart/updateRockChart/${previousStateRef?.current[0]?._id}`,
        {
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        }
      );
    } else {
      PostHooks(
        `${server_url}/rockChart/addRockChart`,
        {
          gameName: "Rock Chart Monocular",
          userId: userInfo?._id,
          userName: userInfo?.name,
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        },
        "rock chart score updated !"
      );
    }
  }, [userInfo, date, time]);

  const insertFunctionRef = useRef(insertFunction);
  useEffect(() => {
    insertFunctionRef.current = insertFunction;
  }, [insertFunction]);

  const generateNewTargetLetters = () => {
    const newTargetLetters = Array.from({ length: 4 }, () =>
      String.fromCharCode(65 + Math.floor(Math.random() * 26))
    );
    setTargetLetters(newTargetLetters);
    setCurrentIndex(0);
  };

  const toggleFullScreenRef = useRef(null);
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((error) => {
          console.error("Error entering full screen:", error);
        });
      }
    }
    setIsFullScreen(!isFullScreen);
  };
  toggleFullScreenRef.current = toggleFullScreen;

  useEffect(() => {
    toggleFullScreen();
    generateNewTargetLetters();
  }, []);

  const handleKeyPress = useCallback((event) => {
    const typedKey = event.key.toUpperCase();
    const currentTarget = targetLettersRef.current[currentIndexRef.current];

    if (typedKey === currentTarget) {
      const newTargetLetters = [...targetLettersRef.current];
      newTargetLetters[currentIndexRef.current] = null;
      setTargetLetters(newTargetLetters);
      setScore((prevScore) => prevScore + 1);

      if (correctAudioRef.current) {
        correctAudioRef.current.currentTime = 0;
        correctAudioRef.current.play().catch((e) => console.log(e));
      }

      if (currentIndexRef.current < 3) {
        setCurrentIndex(currentIndexRef.current + 1);
      } else {
        generateNewTargetLetters();
      }
    } else if (event.key !== "Escape") {
      if (wrongAudioRef.current) {
        wrongAudioRef.current.currentTime = 0;
        wrongAudioRef.current.play().catch((e) => console.log(e));
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    const countdownInterval = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownInterval);
          insertFunctionRef.current();
          navigate(-1);
          if (toggleFullScreenRef.current) toggleFullScreenRef.current();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [navigate]);

  useEffect(() => {
    const handleEscPress = async (event) => {
      if (event.key === "Escape") {
        insertFunctionRef.current();
        await navigate(-1);
        if (toggleFullScreenRef.current) toggleFullScreenRef.current();
      }
    };

    window.addEventListener("keydown", handleEscPress);
    return () => {
      window.removeEventListener("keydown", handleEscPress);
    };
  }, [navigate]);

  const min = Math.floor(timeLeft / 60);
  return (
    <div className="flex inset-0 fixed bg-white flex-col items-center justify-center h-screen ">
      <GameStatsIndicator score={score} timeLeft={timeLeft} />
      <div className="flex justify-center space-x-4">
        {targetLetters.map((letter, index) => (
          <div
            key={index + 1}
            className={` w-28 h-28 rounded-full bg-black flex items-center justify-center text-white text-[85px] ${
              letter === null ? "bg-gray-300 " : ""
            }`}
          >
            {letter === null ? "" : letter}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CircleTypingGame;
